import React, { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { BrandingProvider } from "@/context/BrandingContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CartDrawer from "@/components/CartDrawer";
import Home from "@/pages/Home";
import Shop from "@/pages/Shop";
import CorporateGifts from "@/pages/CorporateGifts";
import ProductDetail from "@/pages/ProductDetail";
import Checkout from "@/pages/Checkout";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Account from "@/pages/Account";
import Admin from "@/pages/Admin";
import OrderSuccess from "@/pages/OrderSuccess";
import Wishlist from "@/pages/Wishlist";
import SharedWishlist from "@/pages/SharedWishlist";
import Campaign from "@/pages/Campaign";
import OrderTrack from "@/pages/OrderTrack";
import CartRedirect from "@/pages/CartRedirect";
import BrandPage from "@/pages/BrandPage";
import ForgotPassword from "@/pages/ForgotPassword";
import AuthCallback from "@/pages/AuthCallback";
import NotFound from "@/pages/NotFound";
import CmsPage from "@/pages/CmsPage";
import OrderPrintView from "@/pages/OrderPrintView";
import DeliveryFeedback from "@/pages/DeliveryFeedback";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsAndConditions from "@/pages/TermsAndConditions";
import { PostLoginFeedbackPrompt } from "@/components/PostLoginFeedbackPrompt";
import { EiraChatWidget } from "@/components/EiraChatWidget";
import { AnalyticsTracker } from "@/components/AnalyticsTracker";
import { CookieConsentBanner } from "@/components/CookieConsentBanner";
import { AccountConsentBanner } from "@/components/AccountConsentBanner";
import { getTrackingConsent } from "@/lib/analytics";
import { RouteSeo } from "@/components/RouteSeo";
const AdminRoute = ({ children }) => {
  const { user, checking } = useAuth();
  if (checking) return null;
  if (user?.role !== "admin") return <Navigate to="/login" replace />;
  return children;
};

const ScrollToTop = () => {
  const { pathname, search } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [pathname, search]);
  return null;
};

const CustomerShell = () => {
  const location = useLocation();
  const { user } = useAuth();
  const adminPath = location.pathname.startsWith("/admin");
  const [trackingConsent, setTrackingConsent] = useState(getTrackingConsent());
  return <>
    <ScrollToTop />
    <RouteSeo />
    <AnalyticsTracker />
    {!adminPath && <CookieConsentBanner onChoice={setTrackingConsent} />}
    {!adminPath && <AccountConsentBanner raisedForCookieConsent={trackingConsent === "unknown"} />}
    {!adminPath && <Header />}
    {!adminPath && <CartDrawer />}
    <main className="min-h-[60vh]">
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/shop" element={<Shop />} />
        <Route path="/corporate-gifts" element={<CorporateGifts />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
        <Route path="/product/:slug" element={<ProductDetail />} />
        <Route path="/checkout" element={<Checkout />} />
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/register" element={<Register />} />
        <Route path="/account" element={<Account />} />
        <Route path="/feedback" element={<DeliveryFeedback />} />
        <Route path="/order/:id" element={<OrderSuccess />} />
        <Route path="/wishlist" element={<Wishlist />} />
        <Route path="/wishlist/shared/:token" element={<SharedWishlist />} />
        <Route path="/campaign/:slug" element={<Campaign />} />
        <Route path="/brand/:slug" element={<BrandPage />} />
        <Route path="/track/:id" element={<OrderTrack />} />
        <Route path="/cart" element={<CartRedirect />} />
        <Route path="/page/:slug" element={<CmsPage />} />
        <Route path="/admin/print" element={<AdminRoute><OrderPrintView /></AdminRoute>} />
        <Route path="/admin/print/:orderId" element={<AdminRoute><OrderPrintView /></AdminRoute>} />
        <Route path="/admin" element={<AdminRoute><Admin /></AdminRoute>} />
        <Route path="/admin/*" element={<AdminRoute><Admin /></AdminRoute>} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </main>
    {!adminPath && <PostLoginFeedbackPrompt />}
    {!adminPath && !user?.consent_required && (
      <EiraChatWidget
        raisedForConsent={trackingConsent === "unknown"}
      />
    )}
    {!adminPath && <Footer />}
  </>;
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <BrandingProvider>
            <CartProvider>
              <CustomerShell />
              <Toaster richColors position="top-right" />
            </CartProvider>
          </BrandingProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
